import * as z from 'zod'

import { AggregatorOccupation } from '~/shared/api/aggregatorOccupation'
import { Taxipark } from '~/shared/api/taxipark'
import {
  CarAccessControlEnum,
  DayEnum,
  DriverStatusEnum,
  InfoSourceEnum,
  RentalContractWorkScheduleEnum,
  TaxSystemEnum,
  TimezoneCode,
} from '~/shared/config/enums'
import {
  dateSchema,
  enumOptionSchema,
  optionSchema,
  phoneScheme,
  snilsScheme,
  stringRequiredScheme,
  uuidOptionSchema,
} from '~/shared/lib/schemas'
import { ApiModel, ToManyRelation, ToOneRelation } from './core'

const attributeSchema = z.object({
  lastName: stringRequiredScheme,
  firstName: stringRequiredScheme,
  middleName: z.string().optional().nullable(),
  email: z
    .string()
    .trim()
    .min(1, 'Обязательное поле')
    .email('Неверный адрес электронной почты'),
  birthDate: dateSchema,
  phone: phoneScheme,
  passportIssueDate: dateSchema,
  passportNumber: stringRequiredScheme,
  passportIssued: stringRequiredScheme,
  citizenship: optionSchema.or(uuidOptionSchema),
  registrationAddress: stringRequiredScheme,
  drivingExperienceStartDate: dateSchema,
  drivingLicenseIssuingCountry: optionSchema.or(uuidOptionSchema),
  drivingLicenseNumber: stringRequiredScheme,
  drivingLicenseIssueDate: dateSchema,
  drivingLicenseExpirationDate: dateSchema,
  extId: z.string(),
  mainTaxiparkId: z.string(),
  emailVerifiedAt: dateSchema.nullable().optional(),
  region: optionSchema.or(uuidOptionSchema),
  placeOfResidence: stringRequiredScheme,
  snils: snilsScheme,
  birthPlace: stringRequiredScheme,
  divisionCode: stringRequiredScheme,
  status: z.nativeEnum(DriverStatusEnum),
  statusComment: z.string().optional().nullable(),
  timezone: enumOptionSchema(TimezoneCode),
  isLead: z.boolean(),
  infoSource: enumOptionSchema(InfoSourceEnum, 'Выберите источник информации')
    .array()
    .min(1, 'Выберите хотя бы один источник информации'),
  availableFuelRefill: z.boolean().optional(),
})

const schema = z
  .object({
    createdAt: dateSchema.optional(),
    updatedAt: dateSchema.optional(),
  })
  .merge(attributeSchema)

export type DriverAttributes = z.infer<typeof attributeSchema>

export type PaymentsTemplateType = {
  time: string
  paymentTime: string
  cost: number
  id: UniqueId
}
export type PaymentsTemplateDataType = {
  paymentsTemplate: {
    data: PaymentsTemplateType[]
  }
  rentalContract: {
    contractNumber: string
    createdAt: string
    rentPerDay: string
    workSchedule: RentalContractWorkScheduleEnum
    dayOff: DayEnum | null
  }
}

export class Driver extends ApiModel<typeof schema, DriverAttributes> {
  static jsonApiType = 'drivers'

  static schema = schema

  getFirstName() {
    return this.getAttribute('firstName')
  }
  getLastName() {
    return this.getAttribute('lastName')
  }
  getMiddleName() {
    return this.getAttribute('middleName')
  }
  getFullName() {
    return [
      this.getAttribute('lastName'),
      this.getAttribute('firstName'),
      this.getAttribute('middleName'),
    ].join(' ')
  }
  getPassportNumber() {
    return this.getAttribute('passportNumber')
  }
  getExitId() {
    return this.getAttribute('extId')
  }
  getPassportIssued() {
    return this.getAttribute('passportIssued')
  }
  getPassportIssueDate() {
    return this.getAttribute('passportIssueDate')
  }
  getPhone() {
    return this.getAttribute('phone')
  }
  getEmail(): string {
    return this.getAttribute('email')
  }
  getEmailVerifiedAt() {
    return this.getAttribute('emailVerifiedAt')
  }
  getBirthDate() {
    return this.getAttribute('birthDate')
  }
  getRegistrationAddress() {
    return this.getAttribute('registrationAddress')
  }
  getCitizenship() {
    return this.getAttribute('citizenship')
  }
  getDrivingLicenseNumber() {
    return this.getAttribute('drivingLicenseNumber')
  }
  getDrivingLicenseIssueDate() {
    return this.getAttribute('drivingLicenseIssueDate')
  }
  getDrivingLicenseExpirationDate() {
    return this.getAttribute('drivingLicenseExpirationDate')
  }
  getDrivingLicenseIssuingCountry() {
    return this.getAttribute('drivingLicenseIssuingCountry')
  }
  getStatus(): DriverStatusEnum {
    return this.getAttribute('status')
  }
  getStatusComment(): string {
    return this.getAttribute('statusComment')
  }
  getTimezone(): TimezoneCode {
    return this.getAttribute('timezone')
  }
  getIsLead(): boolean {
    return this.getAttribute('isLead')
  }
  getTaxSystem(): TaxSystemEnum {
    return this.getAttribute('taxSystem')
  }
  getInn(): string {
    return this.getAttribute('inn')
  }
  getAvailableFuelRefill(): boolean {
    return this.getAttribute('availableFuelRefill') ?? false
  }

  getMainTaxiparkId(): string {
    return this.getAttribute('mainTaxiparkId')
  }
  taxiparks(): ToManyRelation<Taxipark, this> {
    return this.hasMany(Taxipark)
  }
  getTaxiparks(): Taxipark[] {
    return this.getRelation('taxiparks')
  }
  setTaxipark(taxiparkId: UniqueId) {
    const taxipark = new Taxipark()
    taxipark.setApiId(taxiparkId)
    this.setRelation('taxipark', taxipark)
  }

  getCurrentOccupation(): AggregatorOccupation {
    return this.getRelation('currentOccupation')
  }
  currentOccupation(): ToOneRelation<AggregatorOccupation, this> {
    return this.hasOne(AggregatorOccupation)
  }

  occupations(): ToManyRelation<AggregatorOccupation, this> {
    return this.hasMany(AggregatorOccupation)
  }
  getOccupations(): AggregatorOccupation[] {
    return this.getRelation('occupations')
  }
  setOccupation(occupationId: UniqueId) {
    const occupation = new AggregatorOccupation()
    occupation.setApiId(occupationId)
    this.setRelation('occupations', occupation)
  }

  static async me() {
    const response = await Driver.with('taxiparks')
      .with('occupations')
      .with('currentOccupation')
      .find('me')
    return response?.getData()
  }

  static async setMainTaxipark(taxiparkId: UniqueId) {
    const url = `${Driver.getJsonApiUrl()}/set-main-taxipark`
    const client = Driver.httpClient.getImplementingClient()
    return await client.post(url, { taxiparkId })
  }

  static async updateEmail(email: string) {
    const url = `${Driver.getJsonApiUrl()}/update-email`
    const client = Driver.httpClient.getImplementingClient()
    return await client.put(url, { email })
  }

  static async resendVerificationEmailCode() {
    const url = `${Driver.getJsonApiUrl()}/resend-verification-email-code`
    const client = Driver.httpClient.getImplementingClient()
    return await client.post(url)
  }

  static async verifyEmail(code: string) {
    const url = `${Driver.getJsonApiUrl()}/verify-email`
    const client = Driver.httpClient.getImplementingClient()
    return await client.post(url, { code })
  }

  static async verifyEmailByToken(
    token: string,
  ): Promise<{ success: boolean; message: string }> {
    const url = `${Driver.jsonApiBaseUrl}/email-verify`
    const client = Driver.httpClient.getImplementingClient()
    const { data } = await client.get<{ success: boolean; message: string }>(
      url,
      {
        params: { token },
        headers: { Accept: 'application/json' },
      },
    )
    return data
  }

  static async createDeletionRequest() {
    const url = `${Driver.getJsonApiUrl()}/create-deletion-request`
    const client = Driver.httpClient.getImplementingClient()
    return await client.post(url)
  }

  static async carAccessControl(command: CarAccessControlEnum) {
    const url = `${Driver.getJsonApiUrl()}/car-access-control`
    const client = Driver.httpClient.getImplementingClient()
    return await client.post(url, { command })
  }

  static async getPaymentSchedule(): Promise<PaymentsTemplateDataType> {
    const url = `${Driver.getJsonApiUrl()}/payments-template`
    const response = await Driver.effectiveHttpClient.get(url)
    return response.getData()
  }

  static async retryChecks() {
    const url = `${Driver.getJsonApiUrl()}/retry-checks`
    const response = await Driver.effectiveHttpClient.post(url)
    return response.getData()
  }

  async createLead(captchaToken: string) {
    const url = `${Driver.getJsonApiUrl()}/create-lead`
    const client = Driver.httpClient.getImplementingClient()
    const data = this.getJsonApiData()
    return await client.post(url, { ...data, captchaToken })
  }

  static async createUpdateRequest(data: {
    passportPhotos: string[]
    drivingLicensePhotos: string[]
  }) {
    const url = `${Driver.getJsonApiUrl()}/create-update-request`
    const client = Driver.httpClient.getImplementingClient()

    return await client.post(url, data)
  }

  static async uploadMedia(file: Blob): Promise<UniqueId> {
    const url = `${Driver.getJsonApiUrl()}/upload-photo`

    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', 'documents')

    const client = Driver.httpClient.getImplementingClient()
    const { data } = await client.post<{ uuid: UniqueId }>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return data.uuid
  }

  static async updateCurrentOccupation(currentOccupationId: UniqueId) {
    const url = `${Driver.getJsonApiUrl()}/update-current-occupation`
    const client = Driver.httpClient.getImplementingClient()
    return await client.put(url, { currentOccupationId })
  }
}
